import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FileText, Plus, Loader2, Trash2, ArrowLeft, ChevronUp, ChevronDown, Link2, Globe, Lock, Building2, GripVertical, Users, Send, FileDown, Rocket, Camera, Bell, Mail, CheckCircle2, Clock, GitBranch, ImagePlus, Eye, Sparkles, Radio } from "lucide-react";
import { AiFormDialog } from "@/components/AiFormDialog";
import { LiveFormEditor } from "@/components/LiveFormEditor";
import { FIELD_LIBRARY, FIELD_META, fieldNeedsOptions, docStepPhotos, CONDITION_TRIGGER_TYPES, fieldOptionValues, exampleImageUrl } from "@/components/FormFieldKit";

const KIND_LABEL = { brief: "Brief", document_request: "Document Request", general: "General" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function ConsoleForms({ canEdit = true }) {
  const [forms, setForms] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", kind: "brief" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/console/forms"); setForms(data.forms || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setForms([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give the form a title"); return; }
    setSaving(true);
    try { const { data } = await api.post("/console/forms", form); setCreateOpen(false); setForm({ title: "", description: "", kind: "brief" }); await load(); setOpenId(data.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (openId) return <FormEditor fid={openId} canEdit={canEdit} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto" data-testid="console-forms-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] flex items-center gap-2.5"><FileText className="h-7 w-7 text-[#723FFF]" /> Forms</h1>
          <p className="mt-2 text-sm text-[#9FA1A4]">Build brief & document-request forms, deploy them to client workspaces, and collect submissions per client.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button data-testid="form-ai-btn" variant="outline" onClick={() => setAiOpen(true)} className="rounded-full border-[#C9B8FF] text-[#5b2fd6] hover:bg-[#F8F5FF]"><Sparkles className="h-4 w-4 mr-1.5" /> Build with AI</Button>
            <Button data-testid="form-create-btn" onClick={() => setCreateOpen(true)} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New form</Button>
          </div>
        )}
      {aiOpen && <AiFormDialog onClose={() => setAiOpen(false)} onCreated={async (f) => { setAiOpen(false); await load(); setOpenId(f.id); }} />}
      </div>

      {forms === null ? <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto" /></div>
        : forms.length === 0 ? <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-20 text-center"><FileText className="h-10 w-10 text-[#d3d4d8] mx-auto mb-3" /><p className="text-sm text-[#9FA1A4]">No forms yet. Create your first brief or document-request form.</p></div>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((f) => (
              <button key={f.id} data-testid="form-card" onClick={() => setOpenId(f.id)} className="text-left rounded-2xl border border-[#EEEFEF] bg-white p-5 hover:border-[#d9ccff] hover:shadow-sm transition-all">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6] bg-[#EDE7FF] rounded-full px-2 py-0.5">{KIND_LABEL[f.kind] || f.kind}</span>
                  {f.visibility === "public" ? <span className="inline-flex items-center gap-1 text-[10px] text-[#1f9d55] font-mono-data uppercase tracking-wider"><Globe className="h-3 w-3" /> Public</span> : <span className="inline-flex items-center gap-1 text-[10px] text-[#9FA1A4] font-mono-data uppercase tracking-wider"><Lock className="h-3 w-3" /> Private</span>}
                </div>
                <p className="font-display font-semibold text-[#06060C] truncate">{f.title}</p>
                {f.description && <p className="text-xs text-[#9FA1A4] mt-1 line-clamp-2">{f.description}</p>}
                <div className="mt-4 pt-3 border-t border-[#f0f0f3] flex items-center justify-between text-[11px] text-[#595D62]">
                  <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-[#c4c5cc]" /> {f.deployed_count} client{f.deployed_count === 1 ? "" : "s"}</span>
                  <span className="inline-flex items-center gap-1"><Send className="h-3.5 w-3.5 text-[#c4c5cc]" /> {f.submission_count} submission{f.submission_count === 1 ? "" : "s"}</span>
                </div>
              </button>
            ))}
          </div>
        )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="form-create-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileText className="h-5 w-5 text-[#723FFF]" /> New form</DialogTitle><DialogDescription className="text-[#9FA1A4]">Start with a title — you'll add fields next.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-[#595D62] mb-1 block">Title</label><Input data-testid="form-create-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Website Brief" className="border-[#EEEFEF]" /></div>
            <div><label className="text-xs text-[#595D62] mb-1 block">Type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="form-create-kind" className="border-[#EEEFEF]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="brief">Brief form</SelectItem><SelectItem value="document_request">Document request</SelectItem><SelectItem value="general">General</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-[#595D62] mb-1 block">Description (optional)</label><Textarea data-testid="form-create-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="border-[#EEEFEF]" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#EEEFEF] bg-transparent rounded-full">Cancel</Button><Button data-testid="form-create-save" onClick={create} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function FieldRow({ field, canEdit, onChange, onDelete, onMove, condFields = [] }) {
  const meta = FIELD_META[field.type] || { label: field.type };
  const isInput = meta.input !== false;
  const cond = field.condition || null;
  const ctrl = cond ? condFields.find((c) => c.id === cond.field) : null;
  const setCond = (patch) => onChange({ ...field, condition: patch });
  return (
    <div data-testid="form-field-row" className="rounded-xl border border-[#EEEFEF] bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <GripVertical className="h-4 w-4 text-[#d3d4d8]" />
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6] bg-[#EDE7FF] rounded-full px-2 py-0.5">{meta.label}</span>
        {cond && <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#b06a00] bg-[#FFF3DE] rounded-full px-2 py-0.5 inline-flex items-center gap-1"><GitBranch className="h-3 w-3" /> Conditional</span>}
        <div className="ml-auto flex items-center gap-1">
          <button data-testid="form-field-up" onClick={() => onMove(-1)} className="h-6 w-6 rounded flex items-center justify-center text-[#9FA1A4] hover:bg-[#F6F6FD]"><ChevronUp className="h-4 w-4" /></button>
          <button data-testid="form-field-down" onClick={() => onMove(1)} className="h-6 w-6 rounded flex items-center justify-center text-[#9FA1A4] hover:bg-[#F6F6FD]"><ChevronDown className="h-4 w-4" /></button>
          {canEdit && <button data-testid="form-field-delete" onClick={onDelete} className="h-6 w-6 rounded flex items-center justify-center text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      <Input data-testid="form-field-label" value={field.label} onChange={(e) => onChange({ ...field, label: e.target.value })} disabled={!canEdit} placeholder="Field label / question" className="bg-[#F6F6FD] border-[#EEEFEF] text-sm mb-2" />
      {isInput && field.type !== "terms" && field.type !== "heading" && field.type !== "document_photo" && (
        <Input value={field.placeholder || ""} onChange={(e) => onChange({ ...field, placeholder: e.target.value })} disabled={!canEdit} placeholder="Placeholder / helper text (optional)" className="bg-[#F6F6FD] border-[#EEEFEF] text-xs mb-2" />
      )}
      {fieldNeedsOptions(field.type) && (
        <Textarea data-testid="form-field-options" value={(field.options || []).join("\n")} onChange={(e) => onChange({ ...field, options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} disabled={!canEdit} rows={3} placeholder="One option per line" className="bg-[#F6F6FD] border-[#EEEFEF] text-xs mb-2 resize-y" />
      )}
      {field.type === "image" && (
        <label className="flex items-center gap-2 text-xs text-[#595D62] cursor-pointer mb-2"><input type="checkbox" data-testid="form-field-image-capture" checked={!!field.config?.capture} onChange={(e) => onChange({ ...field, config: { ...(field.config || {}), capture: e.target.checked } })} disabled={!canEdit} className="accent-[#723FFF]" /> <span className="inline-flex items-center gap-1"><Camera className="h-3.5 w-3.5 text-[#723FFF]" /> Let users take a photo (camera)</span></label>
      )}
      {field.type === "document_photo" && (
        <DocPhotoSettings field={field} canEdit={canEdit} onChange={onChange} />
      )}
      {isInput && (
        <label className="flex items-center gap-2 text-xs text-[#595D62] cursor-pointer"><input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ ...field, required: e.target.checked })} disabled={!canEdit} className="accent-[#723FFF]" /> Required</label>
      )}
      {canEdit && condFields.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-[#f0f0f3]">
          <label className="flex items-center gap-2 text-xs font-medium text-[#595D62] cursor-pointer">
            <input type="checkbox" data-testid="form-field-cond-toggle" checked={!!cond} onChange={(e) => setCond(e.target.checked ? { field: condFields[0].id, op: "is", value: "" } : null)} className="accent-[#723FFF]" />
            <span className="inline-flex items-center gap-1"><GitBranch className="h-3.5 w-3.5 text-[#b06a00]" /> Only show this field when…</span>
          </label>
          {cond && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select value={cond.field} onValueChange={(v) => setCond({ ...cond, field: v, value: "" })}>
                <SelectTrigger data-testid="form-field-cond-field" className="bg-[#F6F6FD] border-[#EEEFEF] text-xs h-9"><SelectValue placeholder="Choose field" /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF]">{condFields.map((c) => <SelectItem key={c.id} value={c.id}>{c.label || "(untitled)"}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={cond.op || "is"} onValueChange={(v) => setCond({ ...cond, op: v })}>
                <SelectTrigger data-testid="form-field-cond-op" className="bg-[#F6F6FD] border-[#EEEFEF] text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="is">is</SelectItem><SelectItem value="is_not">is not</SelectItem></SelectContent>
              </Select>
              <Select value={cond.value} onValueChange={(v) => setCond({ ...cond, value: v })}>
                <SelectTrigger data-testid="form-field-cond-value" className="bg-[#F6F6FD] border-[#EEEFEF] text-xs h-9"><SelectValue placeholder="Choose value" /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF] max-h-60">{fieldOptionValues(ctrl).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExampleImageField({ example, canEdit, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const ex = example || {};
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/console/forms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange({ ...ex, asset_id: data.asset_id, filename: data.filename }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2.5">
      <label className={`h-16 w-16 shrink-0 rounded-lg border border-dashed border-[#d9ccff] bg-white flex items-center justify-center overflow-hidden ${canEdit ? "cursor-pointer hover:border-[#723FFF]" : ""}`} data-testid={`${testid}-drop`}>
        {ex.asset_id ? <img src={exampleImageUrl(ex.asset_id)} alt="" className="h-full w-full object-cover" /> : (busy ? <Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" /> : <ImagePlus className="h-4 w-4 text-[#c4c5cc]" />)}
        {canEdit && <input type="file" accept="image/*" hidden data-testid={`${testid}-input`} onChange={(e) => upload(e.target.files?.[0])} />}
      </label>
      <div className="flex-1 space-y-1">
        <Textarea data-testid={`${testid}-text`} value={ex.text || ""} onChange={(e) => onChange({ ...ex, text: e.target.value })} disabled={!canEdit} placeholder="Accompanying text — e.g. “Make sure all four corners are visible and text is readable.”" className="bg-white border-[#EEEFEF] text-xs min-h-[44px]" />
        {ex.asset_id && canEdit && <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange({ ...ex, asset_id: null, filename: null })} className="text-[10px] text-rose-600 hover:underline">Remove image</button>}
      </div>
    </div>
  );
}

function DocPhotoSettings({ field, canEdit, onChange }) {
  const steps = Math.max(1, Math.min(6, Number(field.config?.steps) || 1));
  const labels = field.config?.step_labels || [];
  const examples = field.config?.step_examples || [];
  const showExample = !!field.config?.show_example;
  const pillLabel = field.config?.example_pill_label ?? "See Example";
  const patch = (extra) => onChange({ ...field, config: { ...(field.config || {}), ...extra } });
  const setSteps = (n) => {
    const next = Math.max(1, Math.min(6, n || 1));
    patch({ steps: next, step_labels: Array.from({ length: next }).map((_, i) => labels[i] || ""),
            step_examples: Array.from({ length: next }).map((_, i) => examples[i] || null) });
  };
  const setLabel = (i, v) => { const nl = Array.from({ length: steps }).map((_, idx) => labels[idx] || ""); nl[i] = v; patch({ steps, step_labels: nl }); };
  const setExample = (i, ex) => { const ne = Array.from({ length: steps }).map((_, idx) => examples[idx] || null); ne[i] = ex; patch({ step_examples: ne }); };
  return (
    <div className="rounded-lg border border-[#f0f0f3] bg-[#FCFBFF] p-2.5 mb-2 space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-xs text-[#595D62]">Steps (photos to capture)</label>
        <Input data-testid="form-field-doc-steps" type="number" min={1} max={6} value={steps} onChange={(e) => setSteps(Number(e.target.value))} disabled={!canEdit} className="h-7 w-16 bg-white border-[#EEEFEF] text-xs" />
      </div>
      {steps > 1 && (
        <div className="space-y-1.5">
          {Array.from({ length: steps }).map((_, i) => (
            <Input key={i} data-testid={`form-field-doc-step-${i}`} value={labels[i] || ""} onChange={(e) => setLabel(i, e.target.value)} disabled={!canEdit} placeholder={`Step ${i + 1} instruction — e.g. "Upload the front of your ID"`} className="bg-white border-[#EEEFEF] text-xs" />
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-[#595D62] cursor-pointer pt-0.5"><input type="checkbox" data-testid="form-field-doc-multi" checked={!!field.config?.multi} onChange={(e) => patch({ multi: e.target.checked })} disabled={!canEdit} className="accent-[#723FFF]" /> Allow multiple photos per step (multi-page documents)</label>
      <div className="pt-1 border-t border-[#f0f0f3]">
        <label className="flex items-center gap-2 text-xs text-[#595D62] cursor-pointer pt-1.5"><input type="checkbox" data-testid="form-field-doc-example-toggle" checked={showExample} onChange={(e) => patch({ show_example: e.target.checked })} disabled={!canEdit} className="accent-[#723FFF]" /> <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-[#723FFF]" /> Show an example image (with a "See Example" pill)</span></label>
        {showExample && (
          <div className="mt-2 space-y-2.5 pl-1">
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-[#595D62] shrink-0">Pill text</label>
              <Input data-testid="form-field-doc-pill-label" value={pillLabel} onChange={(e) => patch({ example_pill_label: e.target.value })} disabled={!canEdit} placeholder="See Example" className="h-7 bg-white border-[#EEEFEF] text-xs" />
            </div>
            {Array.from({ length: steps }).map((_, i) => (
              <div key={i}>
                {steps > 1 && <p className="text-[10px] font-mono-data uppercase tracking-wider text-[#5b2fd6] mb-1">{labels[i] || `Photo ${i + 1}`} example</p>}
                <ExampleImageField example={examples[i]} canEdit={canEdit} onChange={(ex) => setExample(i, ex)} testid={`form-field-doc-example-${i}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FormEditor({ fid, canEdit, onBack }) {
  const [f, setF] = useState(null);
  const [liveWs, setLiveWs] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState("");
  const [clientUsers, setClientUsers] = useState([]);

  const load = async () => {
    const [d, c] = await Promise.all([api.get(`/console/forms/${fid}`), api.get("/console/clients").catch(() => ({ data: { clients: [] } }))]);
    setF(d.data); setClients(c.data.clients || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fid]);

  // Load client users of deployed workspaces (for private allowed-users picker)
  useEffect(() => {
    if (!f || f.visibility !== "private" || !(f.workspace_ids || []).length) { setClientUsers([]); return; }
    (async () => {
      const all = [];
      for (const wid of f.workspace_ids) {
        try { const { data } = await api.get(`/workspaces/${wid}/detail`); (data.client_users || []).forEach((u) => all.push({ ...u, ws: data.name })); } catch (e) {}
      }
      setClientUsers(all);
    })();
  }, [f?.visibility, JSON.stringify(f?.workspace_ids)]); // eslint-disable-line

  const patch = async (body, silent) => {
    setSaving(true);
    try { const { data } = await api.put(`/console/forms/${fid}`, body); setF(data); if (!silent) toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const addField = (type) => {
    const meta = FIELD_META[type]; if (!meta) return;
    const nf = { id: `f_${Math.random().toString(36).slice(2, 9)}`, type, label: meta.label, placeholder: "", help: "", required: false, options: meta.options ? ["Option 1", "Option 2"] : [], config: type === "document_photo" ? { steps: 1, step_labels: [] } : {} };
    setF((cur) => ({ ...cur, fields: [...(cur.fields || []), nf] }));
    setAddType("");
  };
  const updateField = (i, nf) => setF((cur) => { const fields = [...cur.fields]; fields[i] = nf; return { ...cur, fields }; });
  const deleteField = (i) => setF((cur) => ({ ...cur, fields: cur.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setF((cur) => { const fields = [...cur.fields]; const j = i + dir; if (j < 0 || j >= fields.length) return cur; [fields[i], fields[j]] = [fields[j], fields[i]]; return { ...cur, fields }; });
  const saveFields = () => patch({ title: f.title, description: f.description, kind: f.kind, fields: f.fields });

  const toggleDeploy = async (wid) => {
    const cur = f.workspace_ids || [];
    const next = cur.includes(wid) ? cur.filter((x) => x !== wid) : [...cur, wid];
    try { const { data } = await api.put(`/console/forms/${fid}/deploy`, { workspace_ids: next }); setF(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAllowed = (uid) => {
    const cur = f.allowed_user_ids || [];
    patch({ allowed_user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] }, true);
  };
  const copyLink = () => {
    const url = `${window.location.origin}/form/${f.public_token}`;
    try { navigator.clipboard?.writeText(url); } catch (e) {}
    toast.success("Form link copied");
  };

  if (!f) return <main className="px-6 md:px-10 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto" /></main>;

  return (
    <main className="px-6 md:px-10 py-8 max-w-5xl mx-auto" data-testid="console-form-editor">
      <button onClick={onBack} data-testid="form-editor-back" className="flex items-center gap-1.5 text-sm text-[#5b2fd6] hover:text-[#723FFF] mb-4"><ArrowLeft className="h-4 w-4" /> All forms</button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6] bg-[#EDE7FF] rounded-full px-2 py-0.5">{KIND_LABEL[f.kind]}</span>
            {f.visibility === "public" && <button data-testid="form-copy-link" onClick={copyLink} className="inline-flex items-center gap-1.5 text-xs text-[#5b2fd6] hover:text-[#723FFF]"><Link2 className="h-3.5 w-3.5" /> Copy public link</button>}
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#06060C] truncate">{f.title}</h1>
        </div>
      </div>

      <Tabs defaultValue="build">
        <TabsList className="bg-[#F6F6FD] border border-[#EEEFEF]">
          <TabsTrigger value="build" data-testid="form-tab-build">Build</TabsTrigger>
          <TabsTrigger value="deploy" data-testid="form-tab-deploy">Deploy</TabsTrigger>
          <TabsTrigger value="share" data-testid="form-tab-share">Share</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="form-tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="form-tab-reminders">Reminders</TabsTrigger>
        </TabsList>

        <TabsContent value="build" className="pt-5 space-y-4">
          <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 space-y-3">
            <div><label className="text-xs text-[#595D62] mb-1 block">Title</label><Input data-testid="form-edit-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} disabled={!canEdit} className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
            <div><label className="text-xs text-[#595D62] mb-1 block">Description</label><Textarea data-testid="form-edit-desc" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} disabled={!canEdit} rows={2} className="bg-[#F6F6FD] border-[#EEEFEF] resize-y" /></div>
          </div>

          <div className="space-y-2.5">
            {(f.fields || []).map((fld, i) => (
              <FieldRow key={fld.id} field={fld} canEdit={canEdit} onChange={(nf) => updateField(i, nf)} onDelete={() => deleteField(i)} onMove={(d) => moveField(i, d)}
                condFields={(f.fields || []).slice(0, i).filter((x) => CONDITION_TRIGGER_TYPES.includes(x.type) && fieldOptionValues(x).length > 0).map((x) => ({ id: x.id, label: x.label, type: x.type, options: x.options }))} />
            ))}
            {(f.fields || []).length === 0 && <div className="rounded-xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-8 text-center text-sm text-[#9FA1A4]">No fields yet. Add one below.</div>}
          </div>

          {canEdit && (
            <div className="flex items-center gap-2">
              <Select value={addType} onValueChange={(v) => addField(v)}>
                <SelectTrigger data-testid="form-add-field" className="w-64 bg-white border-[#EEEFEF]"><SelectValue placeholder="+ Add a field…" /></SelectTrigger>
                <SelectContent className="bg-white border-[#EEEFEF] max-h-80">
                  {FIELD_LIBRARY.map((g) => (
                    <React.Fragment key={g.group}>
                      <div className="px-2 py-1 text-[10px] font-mono-data uppercase tracking-wider text-[#9FA1A4]">{g.group}</div>
                      {g.items.map((it) => <SelectItem key={it.type} value={it.type}>{it.label}</SelectItem>)}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="form-save-fields" onClick={saveFields} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full ml-auto">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save form"}</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="deploy" className="pt-5 space-y-3">
          <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 space-y-2.5" data-testid="form-mode">
            <p className="text-sm font-medium text-[#06060C]">Form mode</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[["standard", "Standard", "Each person fills it in and submits their own copy."], ["live", "Live", "One shared copy per client that saves as you go. Signed-in users take turns editing; the agency finalises and submits it."]].map(([m, l, d]) => (
                <label key={m} data-testid={`form-mode-${m}`} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${(f.mode || "standard") === m ? "border-[#723FFF] bg-[#F8F5FF]" : "border-[#EEEFEF]"}`}>
                  <input type="radio" name="mode" checked={(f.mode || "standard") === m} onChange={() => canEdit && patch({ mode: m })} disabled={!canEdit} className="accent-[#723FFF] mt-1" />
                  <span><span className="text-sm font-medium text-[#06060C] flex items-center gap-1.5">{m === "live" && <Radio className="h-3.5 w-3.5 text-[#723FFF]" />}{l}</span><span className="block text-xs text-[#9FA1A4] mt-0.5">{d}</span></span>
                </label>
              ))}
            </div>
            {f.mode === "live" && f.visibility === "public" && <p className="text-[11px] text-amber-700">Live forms always require sign-in, so the public link is switched off while in Live mode.</p>}
          </div>
          <p className="text-sm text-[#595D62]">Select which client workspaces this form appears in. Submissions are stored per client.</p>
          <div className="rounded-2xl border border-[#EEEFEF] bg-white divide-y divide-[#f0f0f3]">
            {clients.length === 0 ? <p className="p-4 text-sm text-[#9FA1A4]">No client workspaces yet.</p> : clients.map((c) => {
              const on = (f.workspace_ids || []).includes(c.id);
              return (
                <label key={c.id} data-testid={`form-deploy-${c.id}`} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FCFCFE]">
                  <input type="checkbox" checked={on} onChange={() => canEdit && toggleDeploy(c.id)} disabled={!canEdit} className="accent-[#723FFF] h-4 w-4" />
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: c.accent_color || "#723FFF" }}>{(c.name || "?")[0]}</span>
                  <span className="text-sm text-[#06060C] flex-1">{c.name}</span>
                  {on && f.mode === "live" && (() => { const lv = (f.deployments || []).find((d) => d.workspace_id === c.id)?.live; return (
                    <>
                      <span data-testid={`form-live-status-${c.id}`} className="text-[11px] text-[#595D62] hidden md:inline">{!lv || lv.status === "not_started" ? "Not started" : lv.status === "finalised" ? `Finalised ${new Date(lv.finalised_at).toLocaleDateString()}` : lv.editing ? `${lv.editing} editing` : lv.last_saved_at ? `Saved by ${lv.last_saved_by}` : "Open"}</span>
                      <Button data-testid={`form-live-open-${c.id}`} size="sm" variant="outline" onClick={(e) => { e.preventDefault(); setLiveWs(c); }} className="rounded-full h-7 text-xs border-[#C9B8FF] text-[#5b2fd6]"><Radio className="h-3 w-3 mr-1" />{lv?.status === "finalised" ? "View" : "Open live form"}</Button>
                    </>
                  ); })()}
                  {on && <Button data-testid={`form-copy-link-${c.id}`} size="sm" variant="outline" onClick={(e) => { e.preventDefault(); const slug = c.slug || (f.deployments || []).find((d) => d.workspace_id === c.id)?.slug; const url = `${window.location.origin}/${slug}/forms/${f.id}`; navigator.clipboard.writeText(url).then(() => toast.success(`Link for ${c.name} copied`), () => toast.message(url)); }} className="rounded-full h-7 text-xs border-[#EEEFEF] text-[#595D62]"><Link2 className="h-3 w-3 mr-1" />Copy link</Button>}
                  {on && <span className="text-[10px] font-mono-data uppercase tracking-wider text-[#1f9d55] bg-[#1f9d55]/10 rounded-full px-2 py-0.5">Deployed</span>}
                </label>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="share" className="pt-5 space-y-4">
          <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 space-y-3">
            <p className="text-sm font-medium text-[#06060C]">Visibility</p>
            <label className="flex items-start gap-3 rounded-xl border border-[#EEEFEF] p-3 cursor-pointer" data-testid="form-visibility-public">
              <input type="radio" name="vis" checked={f.visibility === "public"} onChange={() => canEdit && patch({ visibility: "public" })} disabled={!canEdit} className="accent-[#723FFF] mt-1" />
              <span><span className="text-sm font-medium text-[#06060C] flex items-center gap-1.5"><Globe className="h-4 w-4 text-[#1f9d55]" /> Public</span><span className="block text-xs text-[#9FA1A4] mt-0.5">Anyone with the link can view and submit — no login required.</span></span>
            </label>
            <label className="flex items-start gap-3 rounded-xl border border-[#EEEFEF] p-3 cursor-pointer" data-testid="form-visibility-private">
              <input type="radio" name="vis" checked={f.visibility === "private"} onChange={() => canEdit && patch({ visibility: "private" })} disabled={!canEdit} className="accent-[#723FFF] mt-1" />
              <span><span className="text-sm font-medium text-[#06060C] flex items-center gap-1.5"><Lock className="h-4 w-4 text-[#9FA1A4]" /> Private</span><span className="block text-xs text-[#9FA1A4] mt-0.5">Only the client users you add below can open it — they must log in.</span></span>
            </label>
          </div>

          {f.visibility === "public" ? (
            <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4">
              <p className="text-xs text-[#595D62] mb-1.5">Public form link</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={`${window.location.origin}/form/${f.public_token}`} className="bg-[#F6F6FD] border-[#EEEFEF] text-sm font-mono-data" data-testid="form-public-url" />
                <Button data-testid="form-share-copy" onClick={copyLink} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full shrink-0"><Link2 className="h-4 w-4 mr-1.5" /> Copy</Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4">
              <p className="text-sm font-medium text-[#06060C] flex items-center gap-1.5 mb-1"><Users className="h-4 w-4 text-[#723FFF]" /> Allowed client users</p>
              <p className="text-xs text-[#9FA1A4] mb-3">Pick users from the deployed workspaces. Client admins & managers always have access.</p>
              {clientUsers.length === 0 ? <p className="text-sm text-[#9FA1A4]">Deploy this form to a client first to choose specific users.</p> : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {clientUsers.map((u) => {
                    const on = (f.allowed_user_ids || []).includes(u.id);
                    return (
                      <label key={u.id} data-testid={`form-allowed-${u.id}`} className="flex items-center gap-2.5 rounded-lg border border-[#f0f0f3] bg-[#FCFCFE] px-3 py-2 cursor-pointer">
                        <input type="checkbox" checked={on} onChange={() => canEdit && toggleAllowed(u.id)} disabled={!canEdit} className="accent-[#723FFF]" />
                        <span className="text-sm text-[#06060C] flex-1 truncate">{u.name} <span className="text-[#9FA1A4]">· {u.email}</span></span>
                        <span className="text-[10px] font-mono-data text-[#9FA1A4] truncate">{u.ws}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions" className="pt-5">
          <FormSubmissions fid={fid} />
        </TabsContent>

        <TabsContent value="reminders" className="pt-5">
          <FormReminders fid={fid} form={f} canEdit={canEdit} onFormChange={setF} patch={patch} />
        </TabsContent>
      </Tabs>
      {liveWs && <LiveFormEditor base={`/console/forms/${fid}/live/${liveWs.id}`} uploadUrl="/console/forms/upload" isAgency clientName={liveWs.name} onClose={() => setLiveWs(null)}
        onChanged={() => api.get(`/console/forms/${fid}`).then(({ data }) => setF(data)).catch(() => {})} />}
    </main>
  );
}

function FormReminders({ fid, form, canEdit, onFormChange, patch }) {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const enabled = !!form.reminders_enabled;
  const delay = form.reminder_delay_hours ?? 24;
  const max = form.reminder_max ?? 2;

  const loadStatus = () => api.get(`/console/forms/${fid}/reminders`).then(({ data }) => setStatus(data)).catch(() => setStatus({ recipients: [] }));
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [fid]);

  const setField = (patchBody) => { onFormChange({ ...form, ...patchBody }); };
  const saveSettings = async (patchBody) => { setField(patchBody); await patch(patchBody, true); };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/console/forms/${fid}/remind`);
      if (data.sent > 0) toast.success(`Sent ${data.sent} reminder${data.sent === 1 ? "" : "s"}`);
      else toast.info("No one is due a reminder right now");
      await loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const recips = status?.recipients || [];
  const pending = recips.filter((r) => !r.submitted);
  const eligible = recips.filter((r) => r.eligible);

  return (
    <div className="space-y-4" data-testid="form-reminders">
      <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><Bell className="h-4.5 w-4.5" /></div>
          <div className="flex-1">
            <p className="text-sm font-medium text-[#06060C]">Submission reminders</p>
            <p className="text-xs text-[#9FA1A4] mt-0.5">Email logged-in client users in deployed workspaces who opened this form but haven't submitted yet.</p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
            <input type="checkbox" data-testid="form-reminders-enabled" checked={enabled} onChange={(e) => canEdit && saveSettings({ reminders_enabled: e.target.checked })} disabled={!canEdit} className="accent-[#723FFF] h-4 w-4" />
            <span className="text-xs text-[#595D62]">{enabled ? "On" : "Off"}</span>
          </label>
        </div>

        {enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="text-xs text-[#595D62] mb-1 block">Wait before reminding (hours after they open)</label>
              <Input data-testid="form-reminder-delay" type="number" min={0} max={720} value={delay} onChange={(e) => setField({ reminder_delay_hours: Number(e.target.value) })} onBlur={(e) => saveSettings({ reminder_delay_hours: Math.max(0, Number(e.target.value) || 0) })} disabled={!canEdit} className="bg-[#F6F6FD] border-[#EEEFEF]" />
            </div>
            <div>
              <label className="text-xs text-[#595D62] mb-1 block">Max reminders per person</label>
              <Input data-testid="form-reminder-max" type="number" min={1} max={10} value={max} onChange={(e) => setField({ reminder_max: Number(e.target.value) })} onBlur={(e) => saveSettings({ reminder_max: Math.max(1, Number(e.target.value) || 1) })} disabled={!canEdit} className="bg-[#F6F6FD] border-[#EEEFEF]" />
            </div>
          </div>
        )}
      </div>

      {enabled && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#9FA1A4]">
              {status ? <>{recips.length} opened · {recips.length - pending.length} submitted · <span className="text-[#5b2fd6] font-medium">{eligible.length} due a reminder now</span></> : "Loading…"}
            </p>
            {canEdit && <Button data-testid="form-remind-now" onClick={sendNow} disabled={sending || !eligible.length} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> Send reminders now</>}</Button>}
          </div>

          <div className="rounded-2xl border border-[#EEEFEF] bg-white divide-y divide-[#f0f0f3]">
            {!status ? <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
              : recips.length === 0 ? <p className="p-4 text-sm text-[#9FA1A4]">No client users have opened this form yet. Deploy it to a client workspace and they'll appear here once they open it.</p>
              : recips.map((r) => (
                <div key={r.user_id} data-testid="form-remind-row" className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#06060C] truncate">{r.name} <span className="text-[#9FA1A4]">· {r.email}</span></p>
                    <p className="text-[11px] text-[#9FA1A4]">{r.workspace} · opened {fmtDate(r.opened_at)}{r.reminders_sent > 0 ? ` · reminded ${r.reminders_sent}×` : ""}</p>
                  </div>
                  {r.submitted ? <span className="inline-flex items-center gap-1 text-[11px] text-[#1f9d55] font-mono-data uppercase tracking-wider"><CheckCircle2 className="h-3.5 w-3.5" /> Submitted</span>
                    : r.eligible ? <span className="inline-flex items-center gap-1 text-[11px] text-[#5b2fd6] font-mono-data uppercase tracking-wider"><Bell className="h-3.5 w-3.5" /> Due</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] text-[#9FA1A4] font-mono-data uppercase tracking-wider"><Clock className="h-3.5 w-3.5" /> Waiting</span>}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

function FormSubmissions({ fid }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get(`/console/forms/${fid}/submissions`).then(({ data }) => setData(data)).catch(() => setData({ clients: [], unassigned: [] })); }, [fid]);
  if (!data) return <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>;
  const fields = data.form?.fields || [];
  const groups = [...(data.clients || []), ...((data.unassigned || []).length ? [{ client: { id: "_pub", name: "Public link (unassigned)", slug: "" }, submissions: data.unassigned }] : [])];
  if (!groups.length) return <div className="rounded-2xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-16 text-center text-sm text-[#9FA1A4]">No submissions yet.</div>;
  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.client.id} data-testid="form-sub-client-group">
          <p className="font-display font-semibold text-[#06060C] mb-2 flex items-center gap-2"><Building2 className="h-4 w-4 text-[#723FFF]" /> {g.client.name} <span className="font-mono-data text-[11px] text-[#9FA1A4]">{g.submissions.length}</span></p>
          <div className="rounded-2xl border border-[#EEEFEF] bg-white divide-y divide-[#f0f0f3]">
            {g.submissions.map((s) => (
              <button key={s.id} data-testid="form-sub-row" onClick={() => setOpen({ ...s, fields })} className="w-full text-left flex items-center gap-3 p-3 hover:bg-[#FCFCFE]">
                <div className="min-w-0 flex-1"><p className="text-sm text-[#06060C] truncate">{s.submitted_by_name || "Anonymous"}{s.submitted_by_email && <span className="text-[#9FA1A4]"> · {s.submitted_by_email}</span>}</p><p className="text-[11px] text-[#9FA1A4]">{fmtDate(s.created_at)} · {s.source === "public" ? "Public link" : s.source === "live" ? `Live form${s.contributors ? ` · by ${s.contributors}` : ""}` : "Client"}</p></div>
                <FileDown className="h-4 w-4 text-[#c4c5cc]" />
              </button>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-sub-detail">
          {open && <>
            <DialogHeader><DialogTitle className="font-display">{open.submitted_by_name || "Submission"}</DialogTitle><DialogDescription className="text-[#9FA1A4]">{fmtDate(open.created_at)}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {(open.fields || []).filter((fl) => FIELD_META[fl.type]?.input !== false).map((fl) => {
                const v = (open.data || {})[fl.id];
                return (
                  <div key={fl.id} className="rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] p-3">
                    <p className="text-[11px] font-mono-data uppercase tracking-wider text-[#9FA1A4] mb-1">{fl.label}</p>
                    <SubValue field={fl} value={v} fid={fid} />
                  </div>
                );
              })}
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubValue({ field, value }) {
  const download = async (asset_id, filename) => {
    try {
      const res = await api.get(`/console/forms/asset/${asset_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "download"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Could not download file"); }
  };
  if (value === undefined || value === null || value === "") return <p className="text-sm text-[#c4c5cc]">—</p>;
  if (field.type === "document_photo") {
    const steps = Math.max(1, Number(field.config?.steps) || 1);
    const labels = field.config?.step_labels || [];
    const arr = Array.isArray(value) ? value : [];
    const rows = [];
    for (let i = 0; i < steps; i++) {
      const photos = docStepPhotos(arr[i]);
      const lbl = (arr[i] && arr[i].step_label) || labels[i] || (steps > 1 ? `Photo ${i + 1}` : "");
      photos.forEach((p, j) => rows.push({ lbl: photos.length > 1 ? `${lbl || "Page"} ${j + 1}` : lbl, p }));
    }
    if (!rows.length) return <p className="text-sm text-[#c4c5cc]">—</p>;
    return (
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <button key={i} onClick={() => download(r.p.asset_id, r.p.filename)} className="text-sm text-[#5b2fd6] hover:underline inline-flex items-center gap-1"><FileDown className="h-4 w-4" /> {r.lbl ? `${r.lbl}: ` : ""}{r.p.filename || "Download"}</button>
        ))}
      </div>
    );
  }
  if ((field.type === "file" || field.type === "image") && value?.asset_id) {
    return <button onClick={() => download(value.asset_id, value.filename)} className="text-sm text-[#5b2fd6] hover:underline inline-flex items-center gap-1"><FileDown className="h-4 w-4" /> {value.filename || "Download"}</button>;
  }
  if (Array.isArray(value)) return <p className="text-sm text-[#06060C]">{value.join(", ")}</p>;
  if (value === true) return <p className="text-sm text-[#06060C]">Agreed ✓</p>;
  if (field.type === "color") return <span className="inline-flex items-center gap-2 text-sm text-[#06060C]"><span className="h-4 w-4 rounded border" style={{ background: value }} /> {value}</span>;
  return <p className="text-sm text-[#06060C] whitespace-pre-wrap break-words">{String(value)}</p>;
}
